import React, { useEffect, useMemo, useState } from 'react';
import {
  CreditCard,
  User,
  Lock,
  LogOut,
  Bookmark,
  ChevronRight,
  Mail,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../component/Header';
import Footer from '../component/Footer';
import { useAuth } from '../context/AuthContext';
import { changePassword } from '../services/authService';
import { DashboardData, getDashboard, updateProfile } from '../services/userService';

type ActiveTab = 'Payments' | 'My subscriptions' | 'Account Details' | 'Change Password';

interface FormInputProps {
  label: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
  type?: string;
  required?: boolean;
  disabled?: boolean;
}

const FormInput: React.FC<FormInputProps> = ({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  required = false,
  disabled = false,
}) => (
  <div className="w-full">
    <label className="block text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-2 ml-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      className={`w-full bg-zinc-50 border border-zinc-100 rounded-xl py-3.5 px-5 text-sm transition-all font-medium text-zinc-900 ${
        disabled ? 'cursor-not-allowed opacity-70' : 'focus:outline-none focus:border-amber-500'
      }`}
    />
  </div>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const { logoutUser, setSubscriptionStatus } = useAuth();

  const [activeTab, setActiveTab] = useState<ActiveTab>('Payments');
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [profileFirstName, setProfileFirstName] = useState('');
  const [profileLastName, setProfileLastName] = useState('');
  const [profileDisplayName, setProfileDisplayName] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState('');

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState('');

  const menuItems: { name: ActiveTab; icon: React.ComponentType<{ className?: string }> }[] = [
    { name: 'Payments', icon: CreditCard },
    { name: 'My subscriptions', icon: Bookmark },
    { name: 'Account Details', icon: User },
    { name: 'Change Password', icon: Lock },
  ];

  useEffect(() => {
    const loadDashboard = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('You are not logged in.');
        setLoading(false);
        return;
      }

      try {
        const response = await getDashboard(token);
        setDashboard(response.data);
        const hasActiveSubscription = response.data.subscriptions.some((sub) => {
          const status = (sub.status || '').toLowerCase();
          return !['cancelled', 'expired', 'inactive'].includes(status);
        });
        setSubscriptionStatus(hasActiveSubscription);
        setProfileFirstName(response.data.firstname || '');
        setProfileLastName(response.data.lastname || '');
        setProfileDisplayName(`${response.data.firstname || ''} ${response.data.lastname || ''}`.trim());
      } catch (err: any) {
        setError(err.message || 'Failed to load dashboard data.');
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, [setSubscriptionStatus]);

  const handleLogout = () => {
    logoutUser();
    navigate('/login');
  };

  const fullName = useMemo(() => {
    if (!dashboard) return '';
    return `${dashboard.firstname || ''} ${dashboard.lastname || ''}`.trim();
  }, [dashboard]);

  const subscriptionsTotal = useMemo(() => {
    if (!dashboard) return 0;
    return dashboard.subscriptions.reduce((sum, item) => sum + item.price, 0);
  }, [dashboard]);

  const currentSubscription = dashboard?.subscriptions?.[0] || null;

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileMessage('');

    const trimmedFirstName = profileFirstName.trim();
    const trimmedLastName = profileLastName.trim();
    const trimmedDisplayName = profileDisplayName.trim();

    if (!trimmedFirstName || !trimmedLastName) {
      setProfileMessage('Please fill in all required fields.');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setProfileMessage('You are not logged in.');
      return;
    }

    setProfileSaving(true);
    try {
      const response = await updateProfile(token, {
        firstname: trimmedFirstName,
        lastname: trimmedLastName,
        display_name: trimmedDisplayName,
      });

      setDashboard((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          firstname: trimmedFirstName,
          lastname: trimmedLastName,
        };
      });

      setProfileMessage(response.message || 'Profile updated successfully.');
    } catch (err: any) {
      setProfileMessage(err.message || 'Failed to update profile.');
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordMessage('');

    if (!oldPassword || !newPassword || !confirmNewPassword) {
      setPasswordMessage('Please fill all password fields.');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setPasswordMessage('New password and confirm password do not match.');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setPasswordMessage('You are not logged in.');
      return;
    }

    setPasswordSaving(true);
    try {
      const response = await changePassword(token, {
        old_password: oldPassword,
        new_password: newPassword,
      });
      setPasswordMessage(response.message || 'Password changed successfully.');
      setOldPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (err: any) {
      setPasswordMessage(err.message || 'Failed to change password.');
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <>
      <Header />
      <main className="font-sans text-zinc-900 bg-[#f3f3f3] min-h-screen pt-32 pb-20 px-6 relative">
        <div className="absolute inset-0 top-0 opacity-10 pointer-events-none z-0"></div>

        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="bg-white border border-zinc-100 rounded-[2rem] p-4 mb-8 shadow-sm flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-5">
              <div className="w-16 h-16 bg-amber-500 rounded-2xl flex items-center justify-center text-zinc-900 font-black text-2xl shadow-lg shadow-amber-500/20">
                {(dashboard?.firstname?.[0] || 'U').toUpperCase()}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-xl font-black text-zinc-950 tracking-tight">{fullName || 'User'}</h3>
                  <span className="bg-amber-100 text-amber-700 text-[9px] font-black px-2 py-0.5 rounded-full uppercase">
                    Member
                  </span>
                </div>
                <div className="flex items-center gap-4 mt-1">
                  <p className="flex items-center gap-1.5 text-xs font-bold text-zinc-400">
                    <Mail className="w-3 h-3" /> {dashboard?.email || 'No email'}
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-6 py-3 rounded-2xl font-black text-[10px] text-red-500 hover:bg-red-50 transition-all uppercase tracking-widest border border-transparent hover:border-red-100"
            >
              <LogOut className="w-4 h-4" /> Log Out
            </button>
          </div>

          {loading && (
            <div className="bg-white border border-zinc-100 rounded-2xl p-8 mb-8 text-sm font-bold text-zinc-500">
              Loading dashboard data...
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 rounded-2xl p-4 mb-8 text-sm font-medium">
              {error}
            </div>
          )}

          {!loading && !error && dashboard && (
            <>
              <div className="bg-white p-2 rounded-2xl shadow-sm border border-zinc-100 flex overflow-x-auto hide-scrollbar gap-2 mb-12">
                {menuItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeTab === item.name;
                  return (
                    <button
                      key={item.name}
                      onClick={() => setActiveTab(item.name)}
                      className={`whitespace-nowrap flex-1 min-w-[140px] flex items-center justify-center gap-2.5 px-6 py-4 rounded-xl font-bold text-sm transition-all ${
                        isActive
                          ? 'bg-zinc-950 text-white border border-zinc-950 shadow-md'
                          : 'bg-transparent text-zinc-500 hover:bg-zinc-50 hover:text-zinc-900'
                      }`}
                    >
                      <Icon className={`w-4 h-4 ${isActive ? 'text-white' : 'text-zinc-400'}`} />
                      {item.name}
                    </button>
                  );
                })}
              </div>

              <section className="bg-white border border-zinc-100 rounded-[2.5rem] p-8 md:p-14 shadow-[0_20px_50px_rgba(0,0,0,0.02)] min-h-[500px]">
                <div className="flex items-center gap-3 mb-10">
                  <div className="w-1.5 h-8 bg-amber-500 rounded-full" />
                  <h1 className="text-4xl font-black text-zinc-900 tracking-tighter uppercase">{activeTab}</h1>
                </div>

                {activeTab === 'Payments' && (
                  <div className="border border-zinc-100 rounded-[2rem] overflow-x-auto bg-white">
                    {dashboard.payments.length === 0 ? (
                      <p className="p-8 text-sm font-bold text-zinc-500">No payments found.</p>
                    ) : (
                      <table className="w-full text-left text-xs border-collapse font-bold whitespace-nowrap">
                        <thead>
                          <tr className="bg-zinc-50 border-b border-zinc-100">
                            <th className="px-8 py-5 uppercase tracking-widest text-zinc-400">Payment Ref</th>
                            <th className="px-8 py-5 uppercase tracking-widest text-zinc-400">Date</th>
                            <th className="px-8 py-5 uppercase tracking-widest text-zinc-400">Status</th>
                            <th className="px-8 py-5 uppercase tracking-widest text-zinc-400">Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dashboard.payments.map((payment) => {
                            const status = payment.status.toLowerCase();
                            const isCompleted = status.includes('success') || status.includes('completed');
                            return (
                              <tr
                                key={payment.payment_id}
                                className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50 transition-colors"
                              >
                                <td className="px-8 py-6 text-zinc-500 font-mono text-[11px]">{payment.payment_id}</td>
                                <td className="px-8 py-6 text-zinc-900">{new Date(payment.date).toLocaleString()}</td>
                                <td className="px-8 py-6">
                                  <span
                                    className={`px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest ${
                                      isCompleted ? 'bg-amber-100 text-amber-700' : 'bg-zinc-100 text-zinc-600'
                                    }`}
                                  >
                                    {payment.status}
                                  </span>
                                </td>
                                <td className="px-8 py-6 text-zinc-900">INR {payment.price}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}

                {activeTab === 'My subscriptions' && (
                  <div className="space-y-8">
                    <div className="border border-zinc-200 rounded-2xl overflow-hidden bg-white">
                      {currentSubscription ? (
                        <>
                          <div className="flex justify-between px-6 py-4 border-b border-zinc-100 text-xs font-bold text-zinc-500">
                            <span>Status</span>
                            <span className="uppercase text-zinc-900">{currentSubscription.status}</span>
                          </div>
                          <div className="flex justify-between px-6 py-4 border-b border-zinc-100 text-xs font-bold text-zinc-500">
                            <span>Start Date</span>
                            <span className="text-zinc-900">{new Date(currentSubscription.starts_at).toLocaleDateString()}</span>
                          </div>
                          <div className="flex justify-between px-6 py-4 border-b border-zinc-100 text-xs font-bold text-zinc-500">
                            <span>Next Payment date</span>
                            <span className="text-zinc-900">{new Date(currentSubscription.ends_at).toLocaleDateString()}</span>
                          </div>
                          <div className="flex justify-between px-6 py-4 border-b border-zinc-100 text-xs font-bold text-zinc-500">
                            <span>Payment</span>
                            <span className="text-zinc-900 uppercase">{currentSubscription.payment_status}</span>
                          </div>
                          {/* <div className="flex justify-between px-6 py-4 text-xs font-bold text-zinc-500">
                            <span>Actions</span>
                            <button
                              type="button"
                              className="px-4 py-1.5 rounded-lg border border-zinc-300 text-[10px] font-black uppercase tracking-wider text-zinc-900"
                            >
                              Cancel
                            </button>
                          </div> */}
                        </>
                      ) : (
                        <div className="p-8 text-center">
                          <Bookmark className="w-10 h-10 text-zinc-200 mx-auto mb-4" />
                          <p className="text-zinc-400 font-black uppercase tracking-widest text-[10px]">
                            No subscriptions found
                          </p>
                        </div>
                      )}
                    </div>

                    <div>
                      <h4 className="text-3xl font-black tracking-tighter text-zinc-950 mb-5">
                        Subscription totals
                      </h4>
                      <div className="border border-zinc-200 rounded-2xl overflow-hidden bg-white">
                        <div className="flex justify-between px-6 py-4 border-b border-zinc-100 text-[10px] uppercase tracking-widest font-black text-zinc-400 bg-zinc-50">
                          <span>Product</span>
                          <span>Total</span>
                        </div>
                        {dashboard.subscriptions.length > 0 ? (
                          dashboard.subscriptions.map((sub) => (
                            <div key={sub.id} className="flex justify-between px-6 py-4 border-b border-zinc-100 text-sm font-bold text-zinc-700">
                              <span>{sub.plan_name} - Rebills every month</span>
                              <span className="text-zinc-900">INR {sub.price}</span>
                            </div>
                          ))
                        ) : (
                          <div className="flex justify-between px-6 py-4 border-b border-zinc-100 text-sm font-bold text-zinc-500">
                            <span>No active plan</span>
                            <span>INR 0</span>
                          </div>
                        )}
                        <div className="flex justify-between px-6 py-4 border-b border-zinc-100 text-sm font-bold text-zinc-700">
                          <span>SubTotal:</span>
                          <span className="text-zinc-900">INR {subscriptionsTotal}</span>
                        </div>
                        <div className="flex justify-between px-6 py-4 text-sm font-black text-zinc-900">
                          <span>Totals:</span>
                          <span>INR {subscriptionsTotal}</span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-3xl font-black tracking-tighter text-zinc-950 mb-5">Billing address</h4>
                      <div className="border border-zinc-200 rounded-2xl overflow-hidden bg-white">
                        <div className="flex justify-between px-6 py-4 border-b border-zinc-100 text-sm font-bold text-zinc-500">
                          <span>Address</span>
                          <span className="text-zinc-900">test</span>
                        </div>
                        <div className="flex justify-between px-6 py-4 border-b border-zinc-100 text-sm font-bold text-zinc-500">
                          <span>Country</span>
                          <span className="text-zinc-900">test</span>
                        </div>
                        <div className="flex justify-between px-6 py-4 border-b border-zinc-100 text-sm font-bold text-zinc-500">
                          <span>City</span>
                          <span className="text-zinc-900">test</span>
                        </div>
                        <div className="flex justify-between px-6 py-4 border-b border-zinc-100 text-sm font-bold text-zinc-500">
                          <span>State</span>
                          <span className="text-zinc-900">test</span>
                        </div>
                        <div className="flex justify-between px-6 py-4 text-sm font-bold text-zinc-500">
                          <span>Email</span>
                          <span className="text-zinc-900">{dashboard.email}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'Account Details' && (
                  <form className="max-w-6xl space-y-10" onSubmit={handleProfileUpdate}>
                    {profileMessage && (
                      <div className="bg-zinc-50 border border-zinc-200 text-zinc-700 rounded-xl px-4 py-3 text-sm font-medium">
                        {profileMessage}
                      </div>
                    )}
                    <div className="grid md:grid-cols-2 gap-8">
                      <FormInput
                        label="First Name"
                        value={profileFirstName}
                        onChange={(e) => setProfileFirstName(e.target.value)}
                        placeholder="First Name"
                        required
                      />
                      <FormInput
                        label="Last Name"
                        value={profileLastName}
                        onChange={(e) => setProfileLastName(e.target.value)}
                        placeholder="Last Name"
                        required
                      />
                    </div>
                    <div className="grid md:grid-cols-2 gap-8">
                      <FormInput
                        label="Display Name"
                        value={profileDisplayName}
                        onChange={(e) => setProfileDisplayName(e.target.value)}
                        placeholder="Display Name"
                      />
                      <FormInput
                        label="Email / Username"
                        value={dashboard.email}
                        placeholder="Email"
                        type="email"
                        required
                        disabled
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={profileSaving}
                      className="bg-white text-zinc-950 border border-zinc-300 px-8 py-4 rounded-xl font-bold text-lg text-center hover:bg-amber-500 hover:text-zinc-950 hover:border-amber-500 transition-all flex items-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {profileSaving ? 'Saving...' : 'Save Changes'} <ChevronRight className="w-4 h-4" />
                    </button>
                  </form>
                )}

                {activeTab === 'Change Password' && (
                  <form className="max-w-6xl space-y-8" onSubmit={handlePasswordChange}>
                    {passwordMessage && (
                      <div className="bg-zinc-50 border border-zinc-200 text-zinc-700 rounded-xl px-4 py-3 text-sm font-medium">
                        {passwordMessage}
                      </div>
                    )}
                    <FormInput
                      label="Current Password"
                      type="password"
                      value={oldPassword}
                      onChange={(e) => setOldPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                    />
                    <div className="h-px bg-zinc-100 w-full" />
                    <FormInput
                      label="New Password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Min 8 characters"
                      required
                    />
                    <FormInput
                      label="Confirm New Password"
                      type="password"
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      placeholder="Confirm new password"
                      required
                    />
                    <button
                      type="submit"
                      disabled={passwordSaving}
                      className="bg-white text-zinc-950 border border-zinc-300 px-8 py-4 rounded-xl font-bold text-lg text-center hover:bg-amber-500 hover:text-zinc-950 hover:border-amber-500 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {passwordSaving ? 'Updating...' : 'Update Security Portal'}
                    </button>
                  </form>
                )}
              </section>
            </>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
};

export default Dashboard;
